/**
 * Global Error Boundary Component
 * 
 * Catches unhandled errors in the React component tree and displays
 * a user-friendly error page with recovery options.
 */

import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Logger } from 'src/utils/logger';
import { errorHandlingService } from 'src/services/error-handling';
import { signalPageReady } from 'src/utils/page-ready-signal';

const logger = Logger.createScopedLogger('GlobalErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Global Error Boundary that catches React errors
 */
class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a chunk loading error (dynamic import failure)
    // These typically happen when the browser has cached old JS files after a deployment
    const isChunkLoadError = GlobalErrorBoundary.isChunkLoadError(error);
    
    if (isChunkLoadError) {
      // Auto-reload the page to get fresh assets
      // Use a flag to prevent infinite reload loops
      const reloadAttempted = sessionStorage.getItem('chunk_error_reload');
      if (!reloadAttempted) {
        sessionStorage.setItem('chunk_error_reload', 'true');
        window.location.reload();
        return { hasError: false, error: null };
      }
      // Clear the flag after showing error (user can try manual refresh)
      sessionStorage.removeItem('chunk_error_reload');
    }
    
    return { hasError: true, error };
  }

  /**
   * Detects chunk loading errors from dynamic imports
   * These occur when browser has cached old JS files after deployment
   */
  static isChunkLoadError(error: Error): boolean {
    const message = error.message?.toLowerCase() || '';
    const name = error.name?.toLowerCase() || '';
    
    return (
      message.includes('loading chunk') ||
      message.includes('dynamically imported module') ||
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('error loading dynamically imported module') ||
      name.includes('chunkloaderror') ||
      // Vite-specific error patterns
      message.includes('failed to load module script') ||
      message.includes('mime type')
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error
    logger.error('Uncaught error in component tree', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Process error through our error handling service
    errorHandlingService.handleError(error, {
      context: 'GlobalErrorBoundary',
      showToast: false, // We show a full page error instead
    });

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <GlobalErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
          onRefresh={this.handleRefresh}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default fallback UI for global errors
 */
interface GlobalErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
  onRefresh: () => void;
  onGoHome: () => void;
}

const GlobalErrorFallback: React.FC<GlobalErrorFallbackProps> = ({
  error,
  onReset,
  onRefresh,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const isDev = import.meta.env.DEV;

  /*
   * Terminal screen: lift the native WebView overlay itself, for the same reason NotFoundPage
   * does. When a render throws, the page that would have signalled PAGE_READY is gone, so
   * without this the mobile app holds a white overlay over this error UI. No-op on web.
   */
  useEffect(() => {
    signalPageReady('/error');
  }, []);

  return (
    <div className="tw-flex tw-min-h-screen tw-flex-col tw-items-center tw-justify-center tw-bg-gray-50 tw-px-4">
      <div className="tw-w-full tw-max-w-md tw-text-center">
        {/* Error Icon */}
        <div className="tw-mx-auto tw-mb-6 tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100">
          <svg
            className="tw-h-10 tw-w-10 tw-text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Error Message */}
        <h1 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-gray-900">
          {t('errors:unknown.title', { defaultValue: 'Something went wrong' })}
        </h1>
        <p className="tw-mb-6 tw-text-gray-600">
          {t('errors:unknown.description', { defaultValue: "We're sorry, but something unexpected happened. Please try one of the options below." })}
        </p>

        {/* Dev mode error details */}
        {isDev && error && (
          <div className="tw-mb-6 tw-rounded-lg tw-bg-gray-100 tw-p-4 tw-text-left">
            <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-700">
              Error Details (Development Only):
            </p>
            <pre className="tw-overflow-auto tw-whitespace-pre-wrap tw-text-xs tw-text-red-600">
              {error.message}
            </pre>
            {error.stack && (
              <details className="tw-mt-2">
                <summary className="tw-cursor-pointer tw-text-xs tw-text-gray-500">
                  View Stack Trace
                </summary>
                <pre className="tw-mt-2 tw-max-h-40 tw-overflow-auto tw-whitespace-pre-wrap tw-text-xs tw-text-gray-600">
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-justify-center">
          <button
            onClick={onReset}
            className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-shadow-sm hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-ring-offset-2"
          >
            <svg
              className="tw-mr-2 tw-h-4 tw-w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t('actions:try_again', { defaultValue: 'Try Again' })}
          </button>

          <button
            onClick={onRefresh}
            className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-transparent tw-bg-purple-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-purple-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-ring-offset-2"
          >
            <svg
              className="tw-mr-2 tw-h-4 tw-w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t('actions:refresh_page', { defaultValue: 'Refresh Page' })}
          </button>

          <button
            onClick={onGoHome}
            className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-shadow-sm hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 focus:tw-ring-offset-2"
          >
            <svg
              className="tw-mr-2 tw-h-4 tw-w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            {t('actions:go_to_dashboard', { defaultValue: 'Go to Dashboard' })}
          </button>
        </div>

        {/* Support Link */}
        <p className="tw-mt-6 tw-text-sm tw-text-gray-500">
          {t('errors:support.prefix', { defaultValue: 'If this problem persists, please' })}{' '}
          <a
            href="mailto:support@becreatives.com"
            className="tw-text-purple-600 hover:tw-text-purple-700 hover:tw-underline"
          >
            {t('errors:support.contact', { defaultValue: 'contact support' })}
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export { GlobalErrorBoundary, GlobalErrorFallback };
export default GlobalErrorBoundary;
